import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Card, Input, Button, Tag, Empty, Modal, Drawer } from "antd";
import {
  SearchOutlined, ReloadOutlined, FilterOutlined,
  ClockCircleOutlined, CheckCircleOutlined, SyncOutlined, CloseCircleOutlined,
  InboxOutlined, DownloadOutlined, AppstoreOutlined, UnorderedListOutlined,
  MailOutlined, LinkOutlined, DesktopOutlined, TagOutlined, UserOutlined, EyeOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { fetchTickets, updateTicketStatus } from "../services/ticketService";
import { EMAIL_API_URL } from "../../config";

const PAGE_SIZE = 15;

const STATUS_CFG = {
  open:        { label: "Open",        color: "#2563eb", bg: "#eff6ff",  icon: <InboxOutlined />,        gradient: "linear-gradient(135deg,#2563eb,#1d4ed8)", light: "#dbeafe" },
  in_progress: { label: "In Progress", color: "#d97706", bg: "#fffbeb",  icon: <SyncOutlined spin />,    gradient: "linear-gradient(135deg,#d97706,#b45309)", light: "#fef3c7" },
  resolved:    { label: "Resolved",    color: "#16a34a", bg: "#f0fdf4",  icon: <CheckCircleOutlined />,  gradient: "linear-gradient(135deg,#16a34a,#15803d)", light: "#dcfce7" },
  closed:      { label: "Closed",      color: "#64748b", bg: "#f1f5f9",  icon: <CloseCircleOutlined />,  gradient: "linear-gradient(135deg,#64748b,#475569)", light: "#f1f5f9" },
};

const PRIORITY_CFG = {
  high:   { label: "High",   color: "#ea580c", bg: "#fff7ed", dot: "#ea580c" },
  medium: { label: "Medium", color: "#ca8a04", bg: "#fefce8", dot: "#ca8a04" },
  low:    { label: "Low",    color: "#64748b", bg: "#f8fafc", dot: "#94a3b8" },
};

const TYPE_CFG = {
  bug:              { label: "Bug",              color: "#dc2626" },
  feature_request:  { label: "Feature Request",  color: "#16a34a" },
  billing:          { label: "Billing",          color: "#7c3aed" },
  general_feedback: { label: "General Feedback", color: "#64748b" },
  other:            { label: "Other",            color: "#0891b2" },
};

const statusTag = (s) => {
  const cfg = STATUS_CFG[s] || { label: s, color: "#64748b", bg: "#f1f5f9", icon: <ClockCircleOutlined /> };
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, color: cfg.color, background: cfg.bg, whiteSpace: "nowrap", border: `1px solid ${cfg.color}30` }}>{cfg.icon} {cfg.label}</span>;
};

const priorityBadge = (p) => {
  const cfg = PRIORITY_CFG[p] || { label: p, color: "#64748b", bg: "#f8fafc", dot: "#94a3b8" };
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, color: cfg.color, background: cfg.bg, textTransform: "uppercase", letterSpacing: 0.5, border: `1px solid ${cfg.color}25` }}><span style={{ width: 5, height: 5, borderRadius: "50%", background: cfg.dot }} />{cfg.label}</span>;
};

const typeTag = (t) => {
  const cfg = TYPE_CFG[t] || { label: t || "—", color: "#64748b" };
  return <Tag style={{ fontSize: 10, fontWeight: 600, color: cfg.color, borderColor: `${cfg.color}35`, background: `${cfg.color}0d`, borderRadius: 4, margin: 0, padding: "0 5px" }}>{cfg.label}</Tag>;
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const isOverdue = (t) => {
  if (!t?.created_at || ["resolved", "closed"].includes(t.status)) return false;
  return Date.now() - new Date(t.created_at).getTime() > ({ high: 8, medium: 24, low: 72 }[t.priority] || 24) * 3600000;
};

const initials = (name) => (name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const Shimmer = ({ h = 14, w = "100%", r = 6 }) => (
  <div style={{ height: h, width: w, borderRadius: r, background: "linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
);

const groupByStatus = (tickets) => {
  const cols = {};
  Object.keys(STATUS_CFG).forEach((k) => { cols[k] = []; });
  tickets.forEach((t) => { if (cols[t.status]) cols[t.status].push(t); else cols["open"].push(t); });
  return cols;
};

// ─────────────────────────────────────────────────────────────────────────────
const TicketList = () => {
  const navigate = useNavigate();

  const [view, setView] = useState("kanban");
  const switchView = (v) => { setView(v); localStorage.setItem("tk_view", v); };

  // ── List view state ──
  const [tickets, setTickets]         = useState([]);
  const [total, setTotal]             = useState(0);
  const [hasMore, setHasMore]         = useState(true);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // ── Kanban state ──
  const [columns, setColumns]         = useState(() => groupByStatus([]));
  const [kanbanLoading, setKanbanLoading] = useState(false);
  const [kanbanTotal, setKanbanTotal] = useState(0);

  // ── Filters ──
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch]           = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriority]   = useState("");
  const [typeFilter, setTypeFilter]     = useState("");
  const [quickFilter, setQuickFilter]   = useState("all");
  const [refreshKey, setRefreshKey]     = useState(0);

  // ── Drag remarks modal ──
  const [dragModal, setDragModal]     = useState(false);
  const [dragInfo, setDragInfo]       = useState(null); // { ticket, fromStatus, toStatus }
  const [dragRemarks, setDragRemarks] = useState("");
  const [savingDrag, setSavingDrag]   = useState(false);

  // ── Detail drawer ──
  const [drawer, setDrawer]           = useState(false);
  const [drawerTicket, setDrawerTicket] = useState(null);
  const [drawerLoading] = useState(false);

  // ── Per-customer unread email counts ──
  const [unreadMap, setUnreadMap]     = useState({}); // { conversation_id: count }
  const [totalUnread, setTotalUnread] = useState(0);

  const internalUserId = Number(localStorage.getItem("trade_user_id"));
  const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem("cms_token")}` });

  const pageRef     = useRef(1);
  const scrollRef   = useRef(null);
  const throttleRef = useRef(false);
  const debounceRef = useRef(null);

  const fetchUnreadCounts = useCallback(async () => {
    if (!internalUserId) return;
    try {
      const res = await axios.get(`${EMAIL_API_URL}/api/single-email/unread-count`, {
        params: { internalUserId },
        headers: authHdr(),
      });
      const total = res.data?.count || 0;
      setTotalUnread(total);
      // Per-customer breakdown when backend provides it
      const byCustomer = res.data?.by_customer || res.data?.customers || [];
      if (byCustomer.length > 0) {
        const map = {};
        byCustomer.forEach(({ conversation_id, count }) => {
          if (conversation_id) map[conversation_id] = count;
        });
        setUnreadMap(map);
      }
    } catch { /* silent */ }
  }, [internalUserId]); // eslint-disable-line

  useEffect(() => {
    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts]);


  // ── List fetch (paginated) ──
  const fetchPage = useCallback(async (pg, isReset = false) => {
    if (isReset) setLoading(true); else setLoadingMore(true);
    try {
      const filters = { search, status: quickFilter !== "all" ? quickFilter : statusFilter, priority: priorityFilter, type: typeFilter };
      const res  = await fetchTickets(pg, PAGE_SIZE, filters);
      const data = res?.data || [];
      const tot  = res?.pagination?.total || 0;
      setTotal(tot);
      setTickets((prev) => isReset ? data : [...prev, ...data]);
      setHasMore(pg < (Math.ceil(tot / PAGE_SIZE) || 1));
    } catch { if (isReset) setTickets([]); setHasMore(false); }
    finally { setLoading(false); setLoadingMore(false); }
  }, [search, statusFilter, priorityFilter, typeFilter, quickFilter, refreshKey]);

  // ── Kanban fetch (all, no status filter) ──
  const fetchKanban = useCallback(async () => {
    setKanbanLoading(true);
    try {
      const filters = { search, priority: priorityFilter, type: typeFilter };
      const res  = await fetchTickets(1, 200, filters);
      const data = res?.data || [];
      setKanbanTotal(res?.pagination?.total || data.length);
      setColumns(groupByStatus(data));
    } catch { setColumns(groupByStatus([])); }
    finally { setKanbanLoading(false); }
  }, [search, priorityFilter, typeFilter, refreshKey]);


  useEffect(() => { fetchKanban(); }, [fetchKanban]);

  // Infinite scroll for list
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || loading) return;
    const next = pageRef.current + 1; pageRef.current = next; fetchPage(next, false);
  }, [loadingMore, hasMore, loading, fetchPage]);

  useEffect(() => {
    const el = scrollRef.current; if (!el) return;
    const onScroll = (e) => {
      if (throttleRef.current || !hasMore || loadingMore) return;
      const t = e.target;
      if (t.scrollHeight - t.scrollTop - t.clientHeight < 200) {
        throttleRef.current = true; loadMore();
        setTimeout(() => { throttleRef.current = false; }, 400);
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loadMore, hasMore, loadingMore]);

  const handleSearchChange = (e) => {
    const v = e.target.value; setSearchInput(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(v), 500);
  };

  const handleReset = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchInput(""); setSearch(""); setStatusFilter(""); setPriority(""); setTypeFilter(""); setQuickFilter("all");
    setRefreshKey((k) => k + 1);
  };

  const exportCSV = () => {
    const src = view === "kanban" ? Object.values(columns).flat() : tickets;
    const headers = ["ID", "Subject", "Type", "Priority", "Status", "Customer", "Email", "Created"];
    const rows = src.map((t) => [t.id, t.subject || "", t.type || "", t.priority || "", t.status || "", t.user_name || "", t.user_email || "", t.created_at || ""]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `tickets_${new Date().toISOString().split("T")[0]}.csv`; a.click();
  };

  // ── Drag & drop ──
  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId) return; // same column — reorder only, no status change

    const fromStatus = source.droppableId;
    const toStatus   = destination.droppableId;
    const ticket     = columns[fromStatus][source.index];

    // Optimistically move card
    setColumns((prev) => {
      const next = { ...prev };
      const fromCol = [...prev[fromStatus]];
      const toCol   = [...prev[toStatus]];
      const [moved] = fromCol.splice(source.index, 1);
      toCol.splice(destination.index, 0, { ...moved, status: toStatus });
      next[fromStatus] = fromCol;
      next[toStatus]   = toCol;
      return next;
    });

    // Open remarks modal
    setDragInfo({ ticket, fromStatus, toStatus, destIndex: destination.index });
    setDragRemarks("");
    setDragModal(true);
  };

  const cancelDrag = () => {
    // Revert optimistic move
    if (!dragInfo) return;
    const { ticket, fromStatus, toStatus, destIndex } = dragInfo;
    setColumns((prev) => {
      const next = { ...prev };
      const toCol   = [...prev[toStatus]];
      const fromCol = [...prev[fromStatus]];
      const idx = toCol.findIndex((t) => t.id === ticket.id);
      if (idx !== -1) { const [moved] = toCol.splice(idx, 1); fromCol.splice(0, 0, { ...moved, status: fromStatus }); }
      next[fromStatus] = fromCol;
      next[toStatus]   = toCol;
      return next;
    });
    setDragModal(false); setDragInfo(null); setDragRemarks("");
  };

  const confirmDrag = async () => {
    if (!dragRemarks.trim() || !dragInfo) return;
    setSavingDrag(true);
    try {
      await updateTicketStatus(dragInfo.ticket.id, dragInfo.toStatus, dragRemarks);
      toast.success("Status updated");
      setDragModal(false); setDragInfo(null); setDragRemarks("");
    } catch {
      toast.error("Failed to update status");
      cancelDrag();
    } finally { setSavingDrag(false); }
  };

  const labelStyle = { fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 4 };
  const inputStyle = { height: 36, borderRadius: 8, border: "1px solid #e2e8f0", padding: "0 10px", fontSize: 13, outline: "none", background: "#fff", width: "100%", cursor: "pointer" };
  const displayTotal = view === "kanban" ? kanbanTotal : total;

  return (
    <div className="main-content">
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeSlideUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        .tk-row { transition:background 0.12s,box-shadow 0.12s;cursor:pointer; }
        .tk-row:hover { background:#f8f9ff !important; }
        .tk-qf { border:1.5px solid #e2e8f0;background:#fff;border-radius:20px;padding:5px 16px;font-size:12px;font-weight:600;color:#64748b;cursor:pointer;transition:all 0.15s;white-space:nowrap; }
        .tk-qf:hover { border-color:#6366f1;color:#6366f1;background:#f5f3ff; }
        .tk-qf.active { border-color:#6366f1;background:linear-gradient(135deg,#6366f1,#4f46e5);color:#fff;box-shadow:0 3px 10px rgba(99,102,241,0.3); }
        .tk-view-btn { display:inline-flex;align-items:center;gap:6px;padding:6px 14px;border-radius:10px;font-size:12px;font-weight:600;cursor:pointer;border:1.5px solid #e2e8f0;background:#fff;color:#64748b;transition:all 0.15s; }
        .tk-view-btn.active { background:linear-gradient(135deg,#6366f1,#4f46e5);color:#fff;border-color:transparent;box-shadow:0 3px 10px rgba(99,102,241,0.3); }
        .tk-card { background:#fff;border-radius:14px;border:1px solid #f0f4ff;box-shadow:0 2px 8px rgba(99,102,241,0.06);padding:14px 16px;cursor:pointer;transition:box-shadow 0.15s,transform 0.15s; }
        .tk-card:hover { box-shadow:0 6px 20px rgba(99,102,241,0.14);transform:translateY(-2px); }
        .tk-col-scroll::-webkit-scrollbar { width:4px; }
        .tk-col-scroll::-webkit-scrollbar-track { background:transparent; }
        .tk-col-scroll::-webkit-scrollbar-thumb { background:#cbd5e1;border-radius:10px; }
      `}</style>

      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 46, height: 46, borderRadius: 14, background: "linear-gradient(135deg,#6366f1,#4f46e5)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, boxShadow: "0 6px 20px rgba(99,102,241,0.35)" }}>🎫</div>
                  <div>
                    <h4 style={{ margin: 0, fontWeight: 800, fontSize: 20, color: "#1e293b", letterSpacing: -0.3 }}>Tickets & Feedback</h4>
                    <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>Manage customer support tickets and feedback</p>
                  </div>
                </div>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item"><Link to="/cms/home">CMS</Link></li>
                  <li className="breadcrumb-item active">Tickets</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Filters */}
          <Card style={{ borderRadius: 16, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", marginBottom: 16, border: "1px solid #f0f4ff" }} bodyStyle={{ padding: "18px 22px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FilterOutlined style={{ color: "#6366f1" }} />
                <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Filters</span>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className={`tk-view-btn${view === "kanban" ? " active" : ""}`} onClick={() => switchView("kanban")}><AppstoreOutlined /> Kanban</button>
                <button className={`tk-view-btn${view === "list" ? " active" : ""}`} onClick={() => switchView("list")}><UnorderedListOutlined /> List</button>
              </div>
            </div>
            <div className="row g-3">
              <div className="col-md-3">
                <div style={labelStyle}>Search</div>
                <Input prefix={<SearchOutlined style={{ color: "#94a3b8" }} />} placeholder="Name, email..." value={searchInput} onChange={handleSearchChange} allowClear onClear={() => { setSearchInput(""); setSearch(""); }} style={{ borderRadius: 8, height: 36 }} />
              </div>
              <div className="col-md-4">
                <div style={labelStyle}>Priority</div>
                <select value={priorityFilter} onChange={(e) => setPriority(e.target.value)} style={inputStyle}>
                  <option value="">All Priorities</option>
                  {Object.entries(PRIORITY_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <div style={labelStyle}>Type</div>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={inputStyle}>
                  <option value="">All Types</option>
                  {Object.entries(TYPE_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <Button icon={<ReloadOutlined />} onClick={handleReset} style={{ borderRadius: 8, height: 36 }}>Reset Filters</Button>
            </div>
          </Card>

          {/* ── KANBAN VIEW ── */}
          {view === "kanban" && (
            <div style={{ animation: "fadeSlideUp 0.2s ease" }}>
              {/* Kanban toolbar */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>
                  Total: <b style={{ color: "#1e293b" }}>{kanbanTotal}</b>
                  <span style={{ marginLeft: 10, fontSize: 11, color: "#94a3b8" }}>Drag cards between columns to update status</span>
                </span>
                <Button onClick={exportCSV} icon={<DownloadOutlined />} style={{ height: 34, borderRadius: 8, border: "none", background: "linear-gradient(135deg,#06b6d4,#0891b2)", color: "#fff", fontWeight: 600, fontSize: 12, paddingInline: 14 }}>Export CSV</Button>
              </div>

              {kanbanLoading ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16 }}>
                  {Object.keys(STATUS_CFG).map((k) => (
                    <div key={k} style={{ borderRadius: 16, overflow: "hidden" }}>
                      <Shimmer h={52} r={0} />
                      <div style={{ background: "#f8faff", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                        {[1,2,3].map((i) => <Shimmer key={i} h={110} r={14} />)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, alignItems: "flex-start" }}>
                    {Object.entries(STATUS_CFG).map(([status, cfg]) => {
                      const col = columns[status] || [];
                      return (
                        <div key={status} style={{ borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1px solid #f0f4ff" }}>
                          {/* Column header */}
                          <div style={{ background: cfg.gradient, padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ color: "#fff", fontSize: 15 }}>{cfg.icon}</span>
                              <span style={{ fontWeight: 800, fontSize: 14, color: "#fff", letterSpacing: -0.2 }}>{cfg.label}</span>
                            </div>
                            <span style={{ background: "rgba(255,255,255,0.25)", color: "#fff", fontWeight: 800, fontSize: 12, borderRadius: 20, padding: "2px 10px", backdropFilter: "blur(4px)" }}>{col.length}</span>
                          </div>

                          {/* Droppable column */}
                          <Droppable droppableId={status}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className="tk-col-scroll"
                                style={{
                                  minHeight: 120,
                                  maxHeight: "calc(100vh - 340px)",
                                  overflowY: "auto",
                                  background: snapshot.isDraggingOver ? cfg.light : "#f8faff",
                                  padding: "10px 10px 4px",
                                  transition: "background 0.2s",
                                }}
                              >
                                {col.length === 0 && !snapshot.isDraggingOver && (
                                  <div style={{ textAlign: "center", padding: "30px 0", color: "#cbd5e1", fontSize: 12 }}>
                                    No tickets
                                  </div>
                                )}
                                {col.map((t, idx) => {
                                  const overdue = isOverdue(t);
                                  return (
                                    <Draggable key={String(t.id)} draggableId={String(t.id)} index={idx}>
                                      {(prov, snap) => (
                                        <div
                                          ref={prov.innerRef}
                                          {...prov.draggableProps}
                                          {...prov.dragHandleProps}
                                          className="tk-card"
                                          style={{
                                            ...prov.draggableProps.style,
                                            marginBottom: 8,
                                            opacity: snap.isDragging ? 0.92 : 1,
                                            boxShadow: snap.isDragging ? "0 12px 32px rgba(99,102,241,0.22)" : undefined,
                                            transform: snap.isDragging ? `${prov.draggableProps.style?.transform} rotate(1.5deg)` : prov.draggableProps.style?.transform,
                                          }}
                                          onClick={() => navigate(`/cms/tickets/${t.id}`, { state: { conversation_id: t.conversation_id } })}
                                        >
                                          {/* Overdue + type row */}
                                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                            {t.type ? typeTag(t.type) : <span />}
                                            {overdue && <span style={{ fontSize: 9, fontWeight: 800, background: "#fef2f2", color: "#dc2626", padding: "2px 6px", borderRadius: 4, letterSpacing: 0.5 }}>OVERDUE</span>}
                                          </div>

                                          {/* Subject */}
                                          <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", lineHeight: 1.4, marginBottom: 4 }}>{t.subject || "—"}</div>
                                          <div style={{ fontSize: 10, color: "#94a3b8", fontFamily: "monospace", marginBottom: 10 }}>#{t.id}</div>

                                          {/* Customer */}
                                          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
                                            <div style={{ width: 22, height: 22, borderRadius: "50%", background: "linear-gradient(135deg,#6366f1,#4f46e5)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 9, flexShrink: 0 }}>{initials(t.user_name)}</div>
                                            <div onClick={(e) => { e.stopPropagation(); if (t.user_id) navigate(`/cms/manageUserData/user-360-view/${t.user_id}`, { state: { user_name: t.user_name } }); }} style={{ fontSize: 11, color: "#6366f1", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: t.user_id ? "pointer" : "default" }}>{t.user_name || "—"}</div>
                                          </div>

                                          {/* Footer: priority + mail icon + time */}
                                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            {t.priority ? priorityBadge(t.priority) : <span />}
                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                              <button
                                                onClick={(e) => { e.stopPropagation(); navigate(`/cms/tickets/${t.id}`, { state: { conversation_id: t.conversation_id } }); }}
                                                title="Email thread"
                                                style={{ position: "relative", width: 26, height: 26, borderRadius: 8, border: `1.5px solid ${unreadMap[t.conversation_id] > 0 ? "#f87171" : "#c7d2fe"}`, background: unreadMap[t.conversation_id] > 0 ? "#fef2f2" : "#eef2ff", color: unreadMap[t.conversation_id] > 0 ? "#ef4444" : "#6366f1", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 12 }}
                                              >
                                                <MailOutlined />
                                                {unreadMap[t.conversation_id] > 0 && (
                                                  <span style={{ position: "absolute", top: -6, right: -6, minWidth: 14, height: 14, borderRadius: 7, background: "#ef4444", border: "1.5px solid #fff", color: "#fff", fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px", lineHeight: 1 }}>
                                                    {unreadMap[t.conversation_id] > 9 ? "9+" : unreadMap[t.conversation_id]}
                                                  </span>
                                                )}
                                              </button>
                                              <span style={{ fontSize: 10, color: "#94a3b8" }}>{timeAgo(t.created_at)}</span>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </Draggable>
                                  );
                                })}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </div>
                      );
                    })}
                  </div>
                </DragDropContext>
              )}
            </div>
          )}

          {/* ── LIST VIEW ── */}
          {view === "list" && (() => {
            const allTickets = Object.values(columns).flat();
            const activeStatus = quickFilter !== "all" ? quickFilter : "open";
            const cfg = STATUS_CFG[activeStatus] || STATUS_CFG.open;
            const statusTickets = allTickets.filter(t => t.status === activeStatus);
            return (
              <Card style={{ borderRadius: 16, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid #f0f4ff" }} bodyStyle={{ padding: 0 }}>
                {/* Status tabs toolbar */}
                <div style={{ padding: "14px 22px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {Object.entries(STATUS_CFG).map(([k, c]) => {
                      const count = allTickets.filter(t => t.status === k).length;
                      const isActive = activeStatus === k;
                      return (
                        <button key={k} onClick={() => setQuickFilter(k)}
                          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1.5px solid ${isActive ? c.color : "#e2e8f0"}`, background: isActive ? c.color : "#fff", color: isActive ? "#fff" : "#64748b", transition: "all 0.15s" }}>
                          {c.label}
                          <span style={{ background: isActive ? "rgba(255,255,255,0.3)" : "#f1f5f9", color: isActive ? "#fff" : "#64748b", borderRadius: 20, padding: "0 7px", fontSize: 11, fontWeight: 700 }}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                  <Button onClick={exportCSV} icon={<DownloadOutlined />} style={{ height: 34, borderRadius: 8, border: "none", background: "linear-gradient(135deg,#06b6d4,#0891b2)", color: "#fff", fontWeight: 600, fontSize: 12, paddingInline: 14 }}>Export CSV</Button>
                </div>

                {/* Table */}
                {kanbanLoading ? (
                  <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 8 }}>{[1,2,3,4].map(i => <Shimmer key={i} h={44} />)}</div>
                ) : statusTickets.length === 0 ? (
                  <div style={{ padding: 60, textAlign: "center" }}><Empty description={<span style={{ color: "#94a3b8" }}>No {cfg.label.toLowerCase()} tickets</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} /></div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr>{["#", "Subject", "Customer", "Type", "Priority", "Reported", ""].map((h, i) => (
                        <th key={i} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#64748b", fontSize: 11, borderBottom: "2px solid #f1f5f9", background: "#fafbff", textTransform: "uppercase", letterSpacing: 0.6, whiteSpace: "nowrap" }}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {statusTickets.map((t, idx) => (
                        <tr key={t.id} style={{ borderBottom: "1px solid #f8fafc", cursor: "pointer" }} onClick={() => navigate(`/cms/tickets/${t.id}`, { state: { conversation_id: t.conversation_id } })}>
                          <td style={{ padding: "12px 16px", color: "#cbd5e1", fontSize: 12, fontWeight: 600 }}>{idx + 1}</td>
                          <td style={{ padding: "12px 16px", minWidth: 240 }}>
                            <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 13 }}>{t.subject || "—"}</div>
                            <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace" }}>#{t.id}</div>
                          </td>
                          <td style={{ padding: "12px 16px", minWidth: 160 }}>
                            <div onClick={(e) => { e.stopPropagation(); if (t.user_id) navigate(`/cms/manageUserData/user-360-view/${t.user_id}`, { state: { user_name: t.user_name } }); }} style={{ fontWeight: 600, color: "#6366f1", fontSize: 12, cursor: t.user_id ? "pointer" : "default" }}>{t.user_name || "—"}</div>
                            <div style={{ fontSize: 11, color: "#94a3b8" }}>{t.user_email || ""}</div>
                          </td>
                          <td style={{ padding: "12px 16px" }}>{t.type ? <Tag style={{ fontSize: 10, fontWeight: 600, color: TYPE_CFG[t.type]?.color || "#64748b", borderColor: `${TYPE_CFG[t.type]?.color || "#64748b"}35`, background: `${TYPE_CFG[t.type]?.color || "#64748b"}0d`, borderRadius: 4, margin: 0 }}>{TYPE_CFG[t.type]?.label || t.type}</Tag> : "—"}</td>
                          <td style={{ padding: "12px 16px" }}>{t.priority ? priorityBadge(t.priority) : "—"}</td>
                          <td style={{ padding: "12px 16px", fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>{timeAgo(t.created_at)}</td>
                          <td style={{ padding: "12px 16px" }} onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => navigate(`/cms/tickets/${t.id}`, { state: { conversation_id: t.conversation_id } })} style={{ width: 30, height: 30, borderRadius: 8, border: "1.5px solid #e0e7ff", background: "#eef2ff", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#6366f1", fontSize: 13, cursor: "pointer" }}><EyeOutlined /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            );
          })()}

        </div>
      </div>

      {/* ── Ticket Detail Drawer ── */}
      <Drawer
        open={drawer}
        onClose={() => { setDrawer(false); setDrawerTicket(null); }}
        width={520}
        title={null}
        closable={false}
        styles={{ body: { padding: 0 }, header: { display: "none" } }}
      >
        {drawerTicket && (() => {
          const t = drawerTicket;
          const cfg = STATUS_CFG[t.status] || STATUS_CFG.open;
          const fmtDate = (d) => d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
          return (
            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              {/* Hero */}
              <div style={{ background: cfg.gradient, padding: "24px 24px 20px", position: "relative", overflow: "hidden", flexShrink: 0 }}>
                <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
                <div style={{ position: "relative" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                    <span style={{ fontSize: 10, fontFamily: "monospace", letterSpacing: 1.5, fontWeight: 700, color: "rgba(255,255,255,0.75)", background: "rgba(255,255,255,0.15)", padding: "2px 10px", borderRadius: 6 }}>TICKET #{t.id}</span>
                    <button onClick={() => { setDrawer(false); setDrawerTicket(null); }} style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.15)", color: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>×</button>
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: "#fff", lineHeight: 1.35, marginBottom: 12 }}>{t.subject || "—"}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    {t.type && <Tag style={{ fontSize: 11, fontWeight: 700, color: "#fff", borderColor: "rgba(255,255,255,0.35)", background: "rgba(255,255,255,0.18)", borderRadius: 20, margin: 0 }}>{TYPE_CFG[t.type]?.label || t.type}</Tag>}
                    {t.priority && priorityBadge(t.priority)}
                    {statusTag(t.status)}
                  </div>
                  {/* Customer */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, background: "rgba(255,255,255,0.15)", borderRadius: 10, padding: "10px 14px", border: "1px solid rgba(255,255,255,0.2)" }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 900, fontSize: 12, flexShrink: 0 }}>{initials(t.user_name)}</div>
                    <div>
                      <div onClick={() => { if (t.user_id) { setDrawer(false); navigate(`/cms/manageUserData/user-360-view/${t.user_id}`, { state: { user_name: t.user_name } }); } }} style={{ fontWeight: 700, fontSize: 13, color: "#fff", cursor: t.user_id ? "pointer" : "default" }}>{t.user_name || "—"}</div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.75)" }}>{t.user_email || ""}</div>
                    </div>
                    <button onClick={() => { setDrawer(false); navigate(`/cms/tickets/${t.id}`, { state: { conversation_id: t.conversation_id } }); }} style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 20, border: "none", background: "rgba(255,255,255,0.22)", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", backdropFilter: "blur(4px)" }}>
                      <MailOutlined /> Email
                    </button>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px", display: "flex", flexDirection: "column", gap: 14 }}>
                {drawerLoading ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {[80,60,120,100].map((h,i) => <div key={i} style={{ height: h, borderRadius: 12, background: "linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />)}
                  </div>
                ) : (
                  <>
                    {/* Remarks */}
                    {t.remarks && (
                      <div style={{ background: "linear-gradient(135deg,#f5f3ff,#eef2ff)", borderRadius: 12, padding: "16px 18px", border: "1.5px solid #c7d2fe" }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: "#4f46e5", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>✎ Your Remarks</div>
                        <div style={{ fontSize: 13, color: "#3730a3", lineHeight: 1.75, whiteSpace: "pre-wrap" }}>{t.remarks}</div>
                      </div>
                    )}
                    {/* Description */}
                    {t.description && (
                      <div style={{ background: "#fff", borderRadius: 12, padding: "16px 18px", border: "1px solid #f0f4ff", boxShadow: "0 1px 6px rgba(99,102,241,0.05)" }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: "#6366f1", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>Description</div>
                        <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.85, whiteSpace: "pre-wrap" }}>{t.description}</div>
                      </div>
                    )}
                    {/* Attachments */}
                    {t.attachments?.length > 0 && (
                      <div style={{ background: "#fff", borderRadius: 12, padding: "16px 18px", border: "1px solid #f0f4ff", boxShadow: "0 1px 6px rgba(99,102,241,0.05)" }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: "#6366f1", marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>📎 Attachments ({t.attachments.length})</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          {t.attachments.map((att, idx) => {
                            const isImage = att.mime?.startsWith("image/");
                            const sizeKB = att.size ? (att.size / 1024).toFixed(1) + " KB" : "";
                            return (
                              <div key={idx}>
                                {isImage && (
                                  <a href={att.url} target="_blank" rel="noreferrer">
                                    <img src={att.url} alt={att.filename} style={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 8, border: "1px solid #e8ecff", marginBottom: 6, display: "block", cursor: "pointer" }} />
                                  </a>
                                )}
                                <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f8f9ff", borderRadius: 8, padding: "8px 12px", border: "1px solid #e8ecff" }}>
                                  <span style={{ fontSize: 18 }}>{isImage ? "🖼️" : "📄"}</span>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.filename}</div>
                                    {sizeKB && <div style={{ fontSize: 11, color: "#94a3b8" }}>{sizeKB}</div>}
                                  </div>
                                  <a href={att.url} target="_blank" rel="noreferrer" download={att.filename} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, background: "#eef2ff", color: "#6366f1", textDecoration: "none", flexShrink: 0 }}>
                                    <DownloadOutlined style={{ fontSize: 13 }} />
                                  </a>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {/* Ticket Info */}
                    <div style={{ background: "#fff", borderRadius: 12, padding: "16px 18px", border: "1px solid #f0f4ff", boxShadow: "0 1px 6px rgba(99,102,241,0.05)" }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: "#6366f1", marginBottom: 12, textTransform: "uppercase", letterSpacing: 1, display: "flex", alignItems: "center", gap: 5 }}><TagOutlined /> Ticket Info</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px" }}>
                        {[["Type", t.type ? typeTag(t.type) : "—"], ["Priority", t.priority ? priorityBadge(t.priority) : "—"], ["Status", statusTag(t.status)], ["Reported", fmtDate(t.created_at)], ["Last Updated", fmtDate(t.updated_at)]].map(([label, val]) => (
                          <div key={label} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{label}</span>
                            <span style={{ fontSize: 13, color: "#374151" }}>{val}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Technical Info */}
                    {(t.route_url || t.screen_size || t.user_agent) && (
                      <div style={{ background: "#fff", borderRadius: 12, padding: "16px 18px", border: "1px solid #f0f4ff", boxShadow: "0 1px 6px rgba(99,102,241,0.05)" }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: "#6366f1", marginBottom: 12, textTransform: "uppercase", letterSpacing: 1, display: "flex", alignItems: "center", gap: 5 }}><DesktopOutlined /> Technical Info</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                          {t.route_url && <div style={{ display: "flex", gap: 10 }}><div style={{ width: 28, height: 28, borderRadius: 8, background: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><LinkOutlined style={{ color: "#6366f1", fontSize: 12 }} /></div><div><div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 2 }}>Page URL</div><a href={t.route_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#6366f1", wordBreak: "break-all" }}>{t.route_url}</a></div></div>}
                          {t.screen_size && <div style={{ display: "flex", gap: 10 }}><div style={{ width: 28, height: 28, borderRadius: 8, background: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><DesktopOutlined style={{ color: "#6366f1", fontSize: 12 }} /></div><div><div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 2 }}>Screen Size</div><div style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>{t.screen_size}</div></div></div>}
                          {t.user_agent && <div><div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 5 }}>User Agent</div><div style={{ fontSize: 11, color: "#64748b", fontFamily: "monospace", background: "#f1f5f9", padding: "10px 12px", borderRadius: 8, wordBreak: "break-all", lineHeight: 1.6 }}>{t.user_agent}</div></div>}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })()}
      </Drawer>

      {/* ── Drag Remarks Modal ── */}
      <Modal
        open={dragModal}
        onCancel={cancelDrag}
        footer={null}
        width={460}
        centered
        title={null}
        styles={{ content: { borderRadius: 20, overflow: "hidden", padding: 0 }, body: { padding: 0 } }}
        maskClosable={false}
      >
        {dragInfo && (
          <div>
            {/* Header */}
            <div style={{ background: STATUS_CFG[dragInfo.toStatus]?.gradient, padding: "20px 24px" }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.75)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Update Status</div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>
                {STATUS_CFG[dragInfo.fromStatus]?.label} → {STATUS_CFG[dragInfo.toStatus]?.label}
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 4 }}>{dragInfo.ticket.subject}</div>
            </div>
            {/* Body */}
            <div style={{ padding: "22px 24px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 8 }}>
                Remarks <span style={{ color: "#dc2626" }}>*</span>
              </div>
              <textarea
                rows={4}
                autoFocus
                value={dragRemarks}
                onChange={(e) => setDragRemarks(e.target.value)}
                placeholder="Required — add a note about this status change..."
                style={{ width: "100%", borderRadius: 10, border: `1.5px solid ${dragRemarks.trim() ? "#6366f1" : "#e2e8f0"}`, padding: "10px 14px", fontSize: 13, color: "#374151", resize: "none", outline: "none", fontFamily: "inherit", lineHeight: 1.6, transition: "border-color 0.15s" }}
                onFocus={(e) => { e.target.style.borderColor = "#6366f1"; }}
                onBlur={(e) => { e.target.style.borderColor = dragRemarks.trim() ? "#6366f1" : "#e2e8f0"; }}
              />
              {!dragRemarks.trim() && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>Remarks is required.</div>}
              <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
                <button onClick={cancelDrag} style={{ padding: "8px 20px", borderRadius: 20, border: "1.5px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
                <button
                  disabled={!dragRemarks.trim() || savingDrag}
                  onClick={confirmDrag}
                  style={{ padding: "8px 22px", borderRadius: 20, border: "none", background: !dragRemarks.trim() ? "#e2e8f0" : STATUS_CFG[dragInfo.toStatus]?.gradient, color: !dragRemarks.trim() ? "#94a3b8" : "#fff", fontSize: 13, fontWeight: 700, cursor: !dragRemarks.trim() ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6 }}
                >
                  {savingDrag && <SyncOutlined spin style={{ fontSize: 12 }} />} Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TicketList;
